import { memo, useCallback, useEffect, type FC, type RefObject } from "react";
import { X } from "lucide-react";
import EmailEditor, { type Editor, type EditorRef } from "react-email-editor";
import { useTranslation } from "react-i18next";
import { EDITOR_MIN_HEIGHT } from "../AiCampaignConstants";
import type { GeneratedTemplate } from "../AiCampaignTypes";
import { loadTemplateIntoEditor } from "../EmailEditorUtils";

const EDITOR_OPTIONS = { displayMode: "email" as const };
const SCROLL_LOCK_CLASS = "email-canvas-scroll-lock";

interface EmailCanvasProps {
  editorRef: RefObject<EditorRef>;
  template: GeneratedTemplate | null;
  isExpanded: boolean;
  onCollapse: () => void;
}

const EmailCanvas: FC<EmailCanvasProps> = ({ editorRef, template, isExpanded, onCollapse }) => {
  const { t } = useTranslation();

  const handleReady = useCallback(
    (editor: Editor) => loadTemplateIntoEditor(editor, template),
    [template]
  );

  useEffect(() => {
    if (!isExpanded) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCollapse();
      }
    };
    document.body.classList.add(SCROLL_LOCK_CLASS);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.classList.remove(SCROLL_LOCK_CLASS);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded, onCollapse]);

  return (
    <>
      {isExpanded && <div className="email-canvas__backdrop" onClick={onCollapse} aria-hidden />}
      <div
        className={`email-canvas ${isExpanded ? "email-canvas--expanded" : ""}`}
        role={isExpanded ? "dialog" : undefined}
        aria-modal={isExpanded || undefined}
      >
        {isExpanded && (
          <button
            type="button"
            className="email-canvas__close"
            onClick={onCollapse}
            aria-label={t("campaignFlow.aiWizard.createCampaign.collapseCanvas")}
          >
            <X size={18} aria-hidden />
          </button>
        )}
        <EmailEditor
          ref={editorRef}
          onReady={handleReady}
          minHeight={EDITOR_MIN_HEIGHT}
          options={EDITOR_OPTIONS}
        />
      </div>
    </>
  );
};

export default memo(EmailCanvas);
